import React, { useState, useCallback } from 'react';
import { CameraView } from '@/components/chip-scanner/CameraView';
import { DetectionOverlay } from '@/components/chip-scanner/DetectionOverlay';
import { ResultsCard } from '@/components/chip-scanner/ResultsCard';
import { detectChips, type ChipDetection } from '@/services/chipDetection';
import { useChipColors } from '@/hooks/useChipColors';

type PageState = 'idle' | 'processing' | 'results' | 'error';

export function ChipScannerPage() {
  const [pageState, setPageState] = useState<PageState>('idle');
  const [capturedImage, setCapturedImage] = useState<ImageData | null>(null);
  const [detections, setDetections] = useState<ChipDetection[]>([]);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const { chipColors } = useChipColors();

  const handleCapture = useCallback(async (imageData: ImageData) => {
    setCapturedImage(imageData);
    setPageState('processing');
    setErrorMsg(null);

    // Yield to let the UI show "processing" before blocking on CV
    await new Promise(r => setTimeout(r, 50));

    try {
      // Downsample large images for performance
      let processed = imageData;
      const maxDim = 800;
      if (imageData.width > maxDim || imageData.height > maxDim) {
        const scale = maxDim / Math.max(imageData.width, imageData.height);
        const w = Math.round(imageData.width * scale);
        const h = Math.round(imageData.height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d')!;
        const temp = document.createElement('canvas');
        temp.width = imageData.width;
        temp.height = imageData.height;
        temp.getContext('2d')!.putImageData(imageData, 0, 0);
        ctx.drawImage(temp, 0, 0, w, h);
        processed = ctx.getImageData(0, 0, w, h);
      }

      const result = detectChips(processed, chipColors);
      setDetections(result.chips);

      if (result.chips.length === 0) {
        setErrorMsg('No chips detected. Try better lighting or adjusting color tolerance in Settings.');
        setPageState('error');
      } else {
        setPageState('results');
      }
    } catch (err) {
      setErrorMsg(`Detection failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
      setPageState('error');
    }
  }, [chipColors]);

  const handleReset = () => {
    setPageState('idle');
    setCapturedImage(null);
    setDetections([]);
    setErrorMsg(null);
  };

  const chipColorsConfigured = chipColors.length > 0;

  return (
    <div className="max-w-md mx-auto py-6 px-4 space-y-4 min-h-screen bg-[#f5f7f8]">
      <div className="bg-[#e8f5f0] border border-[#006855]/10 rounded-2xl p-5 mb-4">
        <div className="text-[10px] font-black uppercase tracking-[2px] text-[#006855]/70 mb-1">TOOLS</div>
        <h1 className="text-[22px] font-extrabold text-[#2c2f30] leading-tight">Chip Scanner</h1>
      </div>

      {pageState === 'idle' && (
        <>
          {!chipColorsConfigured && (
            <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm p-4 rounded-xl">
              No chip colors configured. Go to <strong>Settings</strong> to define color→value mappings first.
            </div>
          )}
          <CameraView onCapture={handleCapture} disabled={!chipColorsConfigured} />
        </>
      )}

      {pageState === 'processing' && (
        <div className="flex flex-col items-center justify-center py-12 gap-3">
          <div className="w-10 h-10 border-4 border-[#006855]/30 border-t-[#006855] rounded-full animate-spin" />
          <p className="text-sm text-[#6b7280]">Analyzing chips...</p>
        </div>
      )}

      {pageState === 'results' && capturedImage && (
        <>
          <DetectionOverlay imageData={capturedImage} detections={detections} />
          <ResultsCard detections={detections} chipColors={chipColors} />
          <button
            onClick={handleReset}
            className="w-full py-3 text-sm font-bold text-[#006855] hover:text-[#006855]/80 transition-colors"
          >
            Scan Another
          </button>
        </>
      )}

      {pageState === 'error' && (
        <div className="flex flex-col items-center gap-4 py-8">
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-4 rounded-xl text-center">
            {errorMsg}
          </div>
          {capturedImage && detections.length > 0 && (
            <>
              <DetectionOverlay imageData={capturedImage} detections={detections} />
              <ResultsCard detections={detections} chipColors={chipColors} />
            </>
          )}
          <button
            onClick={handleReset}
            className="text-sm font-bold text-[#006855] hover:text-[#006855]/80 transition-colors"
          >
            Try Again
          </button>
        </div>
      )}
    </div>
  );
}
